import omit from 'lodash/omit'
import type {NonEmptyArray} from '@freckle/non-empty'
import {mkNonEmpty} from '@freckle/non-empty'
import {CONFIG} from '@freckle/educator-entities/ts/common/config'
import {HandledError} from '@freckle/educator-entities/ts/common/exceptions/handled-error'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import type {ProductPathsT} from '@freckle/educator-entities/ts/common/helpers/products'
import type {AutomatedAudioT} from '@freckle/educator-entities/ts/common/models/automated-audio'
import type {ReadAloudSupportT} from '@freckle/educator-entities/ts/common/models/read-aloud-support'
import {
  ensureOk,
  fancyClient,
  fancyClientOmitCredentials,
} from '@freckle/educator-entities/ts/openapi-clients'
import {curriculaClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import {logout} from '@freckle/classroom/ts/common/helpers/logout'
import type {PayloadT} from '@freckle/classroom/ts/common/logic/assign-article'
import type {ConstructedResponseT} from '@freckle/classroom/ts/math/constructed-response/models/constructed-response'
import {displayNotification} from './helpers'

export type SetStudentAudioSupportPayloadT = {
  readAloudSupport: ReadAloudSupportT
  automatedAudio: AutomatedAudioT
  studentId: number
}

type SetStudentStoreLimitPayloadT = {
  studentId: number
  storeLimit: number | null | undefined
}

type TeacherSignupReqT = {
  'first-name': string
  'last-name': string
  email: string
  password: string
  'phone-number': string | null | undefined
  language: LangT | null | undefined
}

const normalRetryPeriod = 30000
const failedRetryPeriod = 5000

const successCallback = () => {
  // remove fogginess and undisplay overlay when the connection is fine
  // classList is not supported by IE < 10
  const retryLayerNode = document.getElementById('retry-layer')
  if (retryLayerNode) {
    retryLayerNode.classList.add('hidden')
  }
  const freckleappNode = document.getElementById('freckleapp')
  if (freckleappNode) {
    freckleappNode.classList.remove('blurred')
  }
}

const failureCallback = () => {
  const msg = `Experiencing connection issues, retrying in ${failedRetryPeriod / 1000}s`
  displayNotification('error', msg, failedRetryPeriod / 1000)
  // classList is not supported by IE < 10
  const retryLayerNode = document.getElementById('retry-layer')
  if (retryLayerNode) {
    retryLayerNode.classList.remove('hidden')
  }
  const freckleappNode = document.getElementById('freckleapp')
  if (freckleappNode) {
    freckleappNode.classList.add('blurred')
  }
}

export function checkInternetConnectivity() {
  const tryReachability = async () => {
    // We are omitting credentials here because
    // if we're in the middle of destroying the session, we don't
    // want a concurrent request to resurrect it. This is rare, but
    // it can keep a deleted teacher logged in.
    return fancyClientOmitCredentials
      .GET('/2/teacher-ping', {
        params: {
          query: {
            commitTimestamp: Number(CONFIG.COMMIT_UNIX_TIMESTAMP),
            commitSHA: CONFIG.COMMIT_SHA,
          },
        },
      })
      .then(ensureOk)
      .then(() => {
        successCallback()
        setTimeout(tryReachability, normalRetryPeriod)
      })
      .catch(() => {
        logout()
        failureCallback()
        // switch to rapid retry mode when the connection is down and prevent
        // user from interacting with the application until it's over
        setTimeout(tryReachability, failedRetryPeriod)
      })
  }

  // try to reach our servers every x secs to validate connection health
  setTimeout(tryReachability, normalRetryPeriod)
}

//
// Create a teacher account
//
export async function signupTeacher(data: TeacherSignupReqT): Promise<void> {
  await fancyClient.POST('/2/teachers/accounts', {body: data}).then(ensureOk)
}

export async function disconnectCleverAtTeacherLevel(teacherId: number): Promise<void> {
  await fancyClient
    .DELETE(`/2/teachers/{TeacherIdpCleverR-TeacherId}/idp/clever`, {
      params: {path: {'TeacherIdpCleverR-TeacherId': teacherId}},
    })
    .then(ensureOk)
}

export async function setStudentAudioSupport(
  student: SetStudentAudioSupportPayloadT,
): Promise<void> {
  await fancyClient
    .PATCH('/2/students/{StudentR-StudentId}', {
      params: {
        path: {
          'StudentR-StudentId': student.studentId,
        },
      },
      body: omit(student, 'studentId'),
    })
    .then(ensureOk)
}

export async function setStudentStoreLimit(payload: SetStudentStoreLimitPayloadT): Promise<void> {
  await fancyClient
    .PATCH('/2/students/{StudentR-StudentId}', {
      params: {
        path: {
          'StudentR-StudentId': payload.studentId,
        },
      },
      body: omit(payload, 'studentId'),
    })
    .then(ensureOk)
}

// Merge student
export async function mergeStudent(
  sourceStudentId: number,
  targetStudentId: number,
  removeSourceCourseMemberships: boolean,
): Promise<void> {
  // Transferring course memberships will put the target student in all the courses
  // that the source student was in. This  is desirable when a merge should result
  // in a "copied" student rather than a "moved" student. Otherwise, the source memberships
  // should be removed
  const queryParams = removeSourceCourseMemberships
    ? {'remove-source-course-memberships': true}
    : {'transfer-course-memberships': true}

  await fancyClient
    .DELETE('/2/students/{StudentR-StudentId}', {
      params: {
        path: {
          'StudentR-StudentId': sourceStudentId,
        },
        query: {
          'duplicate-of-id': targetStudentId,
          ...queryParams,
        },
      },
    })
    .then(ensureOk)
}

// Share students or move them to another course
export function bulkMoveStudents(patch: {
  fromCourseId: number
  courseId: number
  studentIds: Array<number>
}): Promise<void> {
  return fancyClient
    .PATCH('/2/students', {
      body: {
        tag: 'moveStudents',
        ...patch,
      },
    })
    .then(ensureOk)
}

export function enrollStudentInCourse(studentId: number, courseId: number): Promise<void> {
  return fancyClient
    .PATCH('/2/students', {
      body: {
        tag: 'enrollStudents',
        courseId,
        studentIds: [studentId],
      },
    })
    .then(ensureOk)
}

//Share Students
export async function shareStudents(
  payload: Array<{studentId: number; targetTeacherId: number}>,
): Promise<void> {
  fancyClient.POST('/2/student-shares', {
    body: payload,
  })
}

//Dismiss shared or transferred students
export async function dismissSharedOrTransferredStudents(
  payload: Array<{studentId: number}>,
): Promise<void> {
  fancyClient.DELETE('/2/student-shares', {
    body: payload,
  })
}

export const createSocialStudiesAssignment = (payload: PayloadT): Promise<{id: number}[]> =>
  fancyClient
    .POST('/2/social-studies-assignments', {
      body: payload,
    })
    .then(extractData)

export const createScienceAssignment = (payload: PayloadT): Promise<{id: number}[]> =>
  fancyClient
    .POST('/2/science-assignments', {
      body: payload,
    })
    .then(extractData)

export async function transferStudents(
  payload: Array<{studentId: number; targetTeacherId: number}>,
): Promise<void> {
  if (payload.length === 0) {
    throw new Error('transferStudents: Must specify at least one student to transfer')
  }
  fancyClient.POST('/2/student-shares', {
    params: {query: {'should-transfer': true}},
    body: payload,
  })
}

export async function idpMergeStudents(
  courseId: number,
  payload: Array<{studentId: number; idpStudentId: number}>,
): Promise<void> {
  await fancyClient
    .POST('/2/courses/{CoursesIdpStudentMergeR-CourseId}/idp-student-merge', {
      params: {
        path: {
          'CoursesIdpStudentMergeR-CourseId': courseId,
        },
      },
      body: payload,
    })
    .then(ensureOk)
}

export const elaArticleFilterBySearchInput = (
  query: string,
): Promise<NonEmptyArray<string> | null> =>
  curriculaClient
    .GET('/articles/search', {
      params: {query: {query}},
    })
    .then(extractData)
    .then(data => mkNonEmpty(data.articleMatches.map(a => a.articleId)))

export async function updateCourseGradeLevel(courseId: number, gradeLevel: number): Promise<void> {
  await fancyClient
    .PATCH('/2/courses/{CoursesStudentsR-CourseId}/students', {
      params: {
        path: {
          'CoursesStudentsR-CourseId': courseId,
        },
      },
      body: {gradeLevel},
    })
    .then(ensureOk)
}

export const deleteCourseMembership = (courseMembershipId: number): Promise<void> =>
  fancyClient
    .DELETE('/2/course-memberships/{CourseMembershipR-CourseMembershipId}', {
      params: {path: {'CourseMembershipR-CourseMembershipId': courseMembershipId}},
    })
    .then(ensureOk)

export async function sendTeacherReminderEmail(teacherId: number): Promise<void> {
  fancyClient.POST(
    '/2/teachers/{TeacherReminderFinishOnboardingR-TeacherId}/reminders/finish-onboarding',
    {
      params: {path: {'TeacherReminderFinishOnboardingR-TeacherId': teacherId}},
    },
  )
}

export type SaveImpressionData = {
  teacherId: number
  split: string
  treatment: string
  label: string
}

export const saveSplitImpression = ({
  teacherId,
  split,
  treatment,
  label,
}: SaveImpressionData): Promise<void> =>
  fancyClient
    .POST('/2/teachers/{TeacherImpressionsR-TeacherId}/impressions', {
      params: {path: {'TeacherImpressionsR-TeacherId': teacherId}},
      body: {split, treatment, label},
    })
    .then(({response}) => {
      if (!response.ok) {
        // Ad-blockers like to block the `/impressions` route. Impressions should be a best effort, so
        // let's wrap this up as a handled error.
        throw new HandledError('Failed to save split impression')
      }
      Promise.resolve()
    })
    .catch(e => {
      console.error(`Failure saving Split impression ${JSON.stringify(e)}`)
      throw new HandledError('Failed to save split impression', e)
    })

export const getSessionAttempts = (
  productPaths: ProductPathsT,
  sessionId: number,
): Promise<Array<number>> =>
  fancyClient
    .GET(
      '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions/{SubjectProductSessionP-Int64}/attempts',
      {
        params: {
          path: {
            'ProductP-AbbreviatedProduct': productPaths.productPath,
            'SubjectP-Subject': productPaths.subjectPath,
            'SubjectProductSessionP-Int64': sessionId.toString(),
          },
        },
      },
    )
    .then(extractData)

export const getConstructedResponses = (
  mathStandardSetId: string,
): Promise<Array<ConstructedResponseT>> =>
  curriculaClient
    .GET('/math/constructed-responses', {
      params: {
        query: {'standard-set': mathStandardSetId},
      },
    })
    .then(extractData)

// Create or update a user on Northpass
export async function educatorAcademyUser(teacherId: number): Promise<void> {
  fancyClient.PUT('/2/educator-academy/user', {
    body: {teacherId},
  })
}
